import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import VideoPlayer from './VideoPlayer';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';
import AssignmentModal from './AssignmentModal';
import { fetchLectures, fetchAccess } from '../Api';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../index.css';

function decodeHtml(html) {
  const parser = new DOMParser();
  return parser.parseFromString(html, 'text/html').body.textContent;
}

function App() {
  const [lectures, setLectures] = useState([]);
  const [currentLectureId, setCurrentLectureId] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Redirect if not logged in
    const userId = localStorage.getItem('loggedInUserId');
    if (!userId) {
      navigate('/login');
      return;
    }

    // Extract category from query params
    const queryParams = new URLSearchParams(location.search);
    const category = queryParams.get('category')?.trim().toLowerCase();
    const videoIdFromUrl = queryParams.get('video');
    
    if (!category) {
      // If no category passed, optionally redirect or show message
      navigate('/');  // Redirect to home if no category provided
      return;
    }

    const init = async () => {
    try {
      const candidateData = await fetchAccess();

      if (
        candidateData?.status === 200 &&
        Array.isArray(candidateData.data)
      ) {
        // Normalize categories from candidate data and check access
        const hasAccess = candidateData.data.some(
          item =>
            item.course_category?.trim().toLowerCase() === category &&
            parseInt(item.giveAccess) === 1
        );

        if (!hasAccess) {
          // Redirect immediately if no access to the category
          navigate('/AccessDenied', { replace: true });
          return;
        }

        // Access allowed, fetch lectures
        const lectureData = await fetchLectures(category);

        // if (Array.isArray(lectureData) && lectureData.length > 0) {
        //   setLectures(lectureData);
        //   setCurrentLectureId(lectureData[0].id);
        // } else {
        //   setLectures([]);
        // }
        if (Array.isArray(lectureData) && lectureData.length > 0) {
        setLectures(lectureData);

        const found = lectureData.find(
          (lec) => String(lec.id) === String(videoIdFromUrl)
        );

        if (found) {
          setCurrentLectureId(found.id);
        } else {
          const firstId = lectureData[0].id;
          setCurrentLectureId(firstId);
          queryParams.set('video', firstId);
          navigate({ search: queryParams.toString() }, { replace: true });
        }
      } else {
        setLectures([]);
      }
      } else {
        // No valid candidate data — redirect
        navigate('/AccessDenied', { replace: true });
      }
    } catch (error) {
      // On error also redirect or handle gracefully
      console.error('Error fetching access or lectures:', error);
      navigate('/AccessDenied', { replace: true });
    }
  };

  init();

  if (videoIdFromUrl && videoIdFromUrl !== String(currentLectureId)) {
    // setCurrentLectureId(videoIdFromUrl); // Keeps React in sync with URL
  }

}, [navigate, location.search]);

  // Disable right-click and common inspect shortcuts
  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();

    const handleKeyDown = (e) => {
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }
      if (
        (e.ctrlKey || e.metaKey) && 
        (e.shiftKey && ['I', 'J', 'C', 'K'].includes(e.key.toUpperCase()))
      ) {
        e.preventDefault();
        return false;
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        ['U', 'S', 'P'].includes(e.key.toUpperCase())
      ) {
        e.preventDefault();
        return false;
      }
    };

    window.addEventListener('contextmenu', handleContextMenu);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleLectureSelect = (id) => {
    if (id === currentLectureId) return; // no need to reload same lecture

    setIsLoading(true); // Start loader

    setTimeout(() => {
      setCurrentLectureId(id);
      setIsLoading(false);

      const queryParams = new URLSearchParams(location.search);
      queryParams.set('video', id);
      navigate({ search: queryParams.toString() }, { replace: true }); // Update URL
    }, 10); // 10 mili seconds
  };



  const handleAssignmentClick = () => setShowAssignmentModal(true); // Show modal
  const handleCloseModal = () => setShowAssignmentModal(false);   // Hide modal
  const currentLecture = lectures.find((l) => l.id === currentLectureId);

  return (
    <>
      <Header onAssignmentClick={handleAssignmentClick} />
      <div className="container-fluid">
        <div className="row g-0 videobody">
          <div className="col-12 col-lg-8 videoframe">
            {lectures.length === 0 ? (
              <p style={{justifyContent:'center',display:'flex', color:'red'}}>No lectures added in this category...</p>
            ) : isLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ): currentLecture ? (
              <>
                <VideoPlayer videoId={currentLectureId} category={currentLecture?.category} />
                <div className="mt-3">
                  <div className="card shadow-sm border rounded-2 p-3 bg-light">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h5 className="mb-0">{decodeHtml(currentLecture.title)}</h5>
                      <div className="d-flex gap-2 ms-auto">
                        {currentLecture.notes && (
                          <a
                            href={`https://freshskills.in/adminbackend/backend/downloadResource.php?file=${encodeURIComponent(currentLecture.notes)}`}
                            className="btn btn-warning btn-sm p-2 fw-bold fs-6"
                          >
                            Resource
                          </a>
                        )}
                        {currentLecture.assignment && (
                          <a
                            href={currentLecture.assignment}
                            target="_blank"
                            download
                            rel="noopener noreferrer"
                            className="btn btn-dark btn-sm p-2 fw-bold fs-6"
                          >
                            Assignment
                          </a>
                        )}
                      </div>
                    </div>
                    <p className="mb-1"><strong>Instructor:</strong> {currentLecture.instructor}</p>
                    <p className="mb-0">{decodeHtml(currentLecture.description)}</p>
                  </div>
                </div>

              </>
            ) : (
              <p>Loading lecture...</p>
            )}
          </div>

          <div className="col-12 col-lg-4 mt-3 sidebar bg-light rounded-2">
            <Sidebar
              lectures={lectures}
              activeId={currentLectureId}
              onSelect={handleLectureSelect}
            />
          </div>
        </div>
      </div>
      <Footer />
      {showAssignmentModal && <AssignmentModal onClose={handleCloseModal} />}
    </>
  );
}

export default App;
